import { CSSProperties } from 'react';
import { HeaderGroup, TableState, useSortBy, useTable } from 'react-table';
import styled from 'styled-components';
import InfiniteScroll from 'react-infinite-scroll-component';

import { DivFlex, ShadowContainer } from '../../Typhography';
import { Splash } from '../Splash';

const TableContainer = styled.div`
  width: 100%;
  position: relative;
  min-height: 100vh;
`;

const Table = styled.table`
  min-width: 100%;
  border-collapse: collapse;
  position: relative;
`;

const Th = styled.th`
  padding: 0 4px;
  color: ${(props) => props.theme.colors.gray};
  text-align: ${(props) => {
    const startAlign = ['#', 'Puzzle', 'Title', 'Category'];

    if (
      props.children &&
      !startAlign.includes(props.children.toString()) &&
      window.location.toString().includes('/puzzle')
    ) {
      return 'center';
    }

    return 'start';
  }};
`;

const Tr = styled.tr``;

const Td = styled.td`
  padding: 10px 0;
  border-top: 1px solid ${(props) => props.theme.colors.lightGray};
`;

export const DeleteButton = styled.p`
  color: ${(props) => props.theme.colors.red};
  ${(props) => props.theme.typography.fontBold};
  cursor: pointer;
`;

export const EditButton = styled.p`
  color: ${(props) => props.theme.colors.gray};
  ${(props) => props.theme.typography.fontBold};
  cursor: pointer;
`;

export const ShadowContainerTable = styled(ShadowContainer)`
  margin: 0px;
  padding: 40px;
  box-sizing: border-box;
`;

export const ActionButtons = styled(DivFlex)`
  justify-content: flex-start;
  gap: 1rem;
`;

export const ButtonPagination = styled.button`
  border-radius: 50%;
  width: 40px;
  height: 40px;
  border: 1px solid ${(props) => props.theme.colors.lightGray};
  background-color: #fff;
  ${(props) => props.theme.typography.fontBold};
  color: ${(props) => props.theme.colors.gray};
  transition: 0.3s;

  &:hover {
    background-color: ${(props) => props.theme.colors.purple};
    color: ${(props) => props.theme.colors.white};
  }

  &:disabled {
    background-color: ${(props) => props.theme.colors.white};
    color: ${(props) => props.theme.colors.lightGray};
  }
`;

export const PaginationButtons = styled.div`
  position: absolute;
  right: 5px;
  bottom: -100px;
`;

interface TableComponentProps {
  columns: any;
  data: any;
  fetchMore?: any;
  loadingMore?: boolean;
  initialState?: Partial<TableState>;
  className?: string;
}

interface OriginalRowProps {
  id?: string;
  __onRowClick__?: () => void; //Setting up strange "private - reserved" name so we don't interfere with dynamically passed props.
}

interface IHeaderGroup extends HeaderGroup {
  onHeaderClick?: () => void;
  style?: CSSProperties;
}

export const TableComponent = ({
  columns,
  data,
  fetchMore,
  loadingMore,
  initialState,
  className,
}: TableComponentProps) => {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable<OriginalRowProps>(
      {
        columns,
        data,
        initialState,
        disableSortRemove: true,
        autoResetSortBy: false,
      },
      useSortBy,
    );

  return (
    <TableContainer className={className}>
      <InfiniteScroll
        loader={loadingMore && <Splash width={25} height={25} position="relative" />}
        dataLength={rows.length}
        next={fetchMore}
        hasMore={true}
        scrollThreshold={0.7}
      >
        <Table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => {
              const { key, ...restHeaderGroupProps } = headerGroup.getHeaderGroupProps();

              return (
                <Tr key={key} {...restHeaderGroupProps}>
                  {(headerGroup.headers as IHeaderGroup[]).map((column) => {
                    const { key, ...restColumn } = column.getHeaderProps({
                      ...column.getSortByToggleProps(),
                      style: {
                        ...column.getSortByToggleProps().style,
                        width: column.width,
                        minWidth: column.minWidth,
                        maxWidth: column.maxWidth,
                      },
                    });

                    return (
                      <Th
                        key={key}
                        {...restColumn}
                        style={{
                          ...restColumn?.style,
                          ...column.style,
                        }}
                        {...(column.onHeaderClick
                          ? { onClick: () => column?.onHeaderClick?.() }
                          : {})}
                      >
                        {column.render('Header')}
                      </Th>
                    );
                  })}
                </Tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps}>
            {rows.map((row, i) => {
              prepareRow(row);
              const { key, ...restRowProps } = row.getRowProps();

              //Row onClick handler.
              let onClick;
              if (row?.original?.__onRowClick__) {
                onClick = row.original.__onRowClick__;
              }

              return (
                <tr
                  key={row?.original?.id ?? key}
                  {...restRowProps}
                  style={{
                    background: i % 2 === 0 ? '#8080800f' : 'white',
                  }}
                  {...(onClick ? { onClick } : {})}
                >
                  {row.cells.map((cell) => {
                    const { key, ...restCellProps } = cell.getCellProps({
                      style: {
                        width: cell.column.width,
                        minWidth: cell.column.minWidth,
                        maxWidth: cell.column.maxWidth,
                      },
                    });
                    return (
                      <Td key={key} {...restCellProps}>
                        {cell.render('Cell')}
                      </Td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </InfiniteScroll>
    </TableContainer>
  );
};
